import { supabase } from './supabase';

const isMissingRelationError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const code = 'code' in error && typeof error.code === 'string' ? error.code : '';
  const message = 'message' in error && typeof error.message === 'string' ? error.message : '';
  const details = 'details' in error && typeof error.details === 'string' ? error.details : '';
  const status = 'status' in error && typeof error.status === 'number' ? error.status : 0;

  return (
    code === 'PGRST205' ||
    code === '42P01' || // PostgreSQL undefined_table
    status === 404 ||
    status === 406 || // Supabase can also return 406 for some missing tables
    message.toLowerCase().includes('could not find the table') ||
    details.toLowerCase().includes('could not find the table') ||
    message.toLowerCase().includes('not ok')
  );
};

const withMissingRelationFallback = async <T>(
  operation: () => Promise<{ data: T | null; error: any }>,
  fallbackData: T
): Promise<{ data: T; error: null } | { data: null; error: any }> => {
  try {
    const { data, error } = await operation();

    if (error) {
      throw error;
    }

    return { data: (data ?? fallbackData) as T, error: null };
  } catch (error) {
    if (isMissingRelationError(error)) {
      return { data: fallbackData, error: null };
    }

    throw error;
  }
};

/** Resolve user_profiles.id for FK columns (cms_media.uploaded_by, etc.). */
async function getUserProfileId(authUserId: string): Promise<string | null> {
  const { data: profile } = await supabase
    .from('user_profiles')
    .select('id')
    .eq('user_id', authUserId)
    .maybeSingle();

  if (profile?.id) {
    return profile.id;
  }

  const { data: newProfile, error: insertError } = await supabase
    .from('user_profiles')
    .insert({ user_id: authUserId, role: 'buyer' })
    .select('id')
    .single();

  if (insertError) {
    console.warn('Could not create user profile for CMS upload:', insertError);
    return null;
  }

  return newProfile?.id ?? null;
}

// Types for CMS content
export interface CMSHeroSection {
  id?: string;
  title: string;
  subtitle?: string;
  cta_text?: string;
  cta_link?: string;
  background_image?: string;
  gradient_start?: string;
  gradient_end?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSService {
  id?: string;
  icon_name: string;
  title: string;
  description: string;
  gradient_start: string;
  gradient_end: string;
  shadow_color: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSBrand {
  id?: string;
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSProcessStep {
  id?: string;
  icon_name: string;
  title: string;
  description: string;
  color: string;
  highlight_text?: string;
  step_number: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSCustomer {
  id?: string;
  name: string;
  logo_url: string;
  website_url?: string;
  description?: string;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSFeaturedEquipment {
  id?: string;
  product_id?: string;
  title_override?: string;
  description_override?: string;
  is_featured?: boolean;
  display_order?: number;
  custom_badge?: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
  product?: any; // Will be joined from products table
}

export interface CMSTestimonial {
  id?: string;
  customer_name: string;
  customer_title?: string;
  customer_company?: string;
  customer_image?: string;
  testimonial_text: string;
  rating?: number;
  is_active?: boolean;
  display_order?: number;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSSiteSetting {
  id?: string;
  setting_key: string;
  setting_value: any;
  setting_type: 'text' | 'number' | 'boolean' | 'json' | 'image';
  description?: string;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface CMSFooterSection {
  id?: string;
  section_title: string;
  section_type: 'links' | 'contact' | 'social' | 'newsletter';
  content: any;
  display_order?: number;
  is_active?: boolean;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

export interface CMSPageMeta {
  id?: string;
  page_slug: string;
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  keywords?: string[];
  custom_meta?: any;
  created_at?: string;
  updated_at?: string;
  updated_by?: string;
}

export interface CMSMedia {
  id?: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size?: number;
  alt_text?: string;
  caption?: string;
  folder?: string;
  uploaded_by?: string;
  created_at?: string;
}

class CMSServiceClass {
  // Hero Sections
  async getHeroSections(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_hero_sections').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async getHeroSectionById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_hero_sections')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching hero section:', error);
      return { data: null, error };
    }
  }

  async createHeroSection(section: CMSHeroSection) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_hero_sections')
        .insert({
          ...section,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating hero section:', error);
      return { data: null, error };
    }
  }

  async updateHeroSection(id: string, updates: Partial<CMSHeroSection>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_hero_sections')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating hero section:', error);
      return { data: null, error };
    }
  }

  async deleteHeroSection(id: string) {
    try {
      const { error } = await supabase
        .from('cms_hero_sections')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting hero section:', error);
      return { error };
    }
  }

  // Services
  async getServices(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_services').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async getServiceById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_services')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching service:', error);
      return { data: null, error };
    }
  }

  async createService(service: CMSService) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_services')
        .insert({
          ...service,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating service:', error);
      return { data: null, error };
    }
  }

  async updateService(id: string, updates: Partial<CMSService>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_services')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating service:', error);
      return { data: null, error };
    }
  }

  async deleteService(id: string) {
    try {
      const { error } = await supabase
        .from('cms_services')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting service:', error);
      return { error };
    }
  }

  // Brands
  async getBrands(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_brands').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async getBrandById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_brands')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching brand:', error);
      return { data: null, error };
    }
  }

  async createBrand(brand: CMSBrand) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_brands')
        .insert({
          ...brand,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating brand:', error);
      return { data: null, error };
    }
  }

  async updateBrand(id: string, updates: Partial<CMSBrand>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_brands')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating brand:', error);
      return { data: null, error };
    }
  }

  async deleteBrand(id: string) {
    try {
      const { error } = await supabase
        .from('cms_brands')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting brand:', error);
      return { error };
    }
  }

  // Customers
  async getCustomers(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_customers').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async getCustomerById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_customers')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching customer:', error);
      return { data: null, error };
    }
  }

  async createCustomer(customer: CMSCustomer) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_customers')
        .insert({
          ...customer,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating customer:', error);
      return { data: null, error };
    }
  }

  async updateCustomer(id: string, updates: Partial<CMSCustomer>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_customers')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating customer:', error);
      return { data: null, error };
    }
  }

  async deleteCustomer(id: string) {
    try {
      const { error } = await supabase
        .from('cms_customers')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting customer:', error);
      return { error };
    }
  }

  // Process Steps
  async getProcessSteps(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_process_steps').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('step_number', { ascending: true });

      return await query;
    }, []);
  }

  async getProcessStepById(id: string) {
    try {
      const { data, error } = await supabase
        .from('cms_process_steps')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching process step:', error);
      return { data: null, error };
    }
  }

  async createProcessStep(step: CMSProcessStep) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_process_steps')
        .insert({
          ...step,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating process step:', error);
      return { data: null, error };
    }
  }

  async updateProcessStep(id: string, updates: Partial<CMSProcessStep>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_process_steps')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating process step:', error);
      return { data: null, error };
    }
  }

  async deleteProcessStep(id: string) {
    try {
      const { error } = await supabase
        .from('cms_process_steps')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting process step:', error);
      return { error };
    }
  }

  // Featured Equipment
  async getFeaturedEquipment(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase
        .from('cms_featured_equipment')
        .select(`
          *,
          product:products(
            id,
            title,
            description,
            price,
            zetta_price,
            images,
            category,
            condition,
            status
          )
        `);

      if (activeOnly) {
        query = query.eq('is_featured', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async createFeaturedEquipment(featured: CMSFeaturedEquipment) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_featured_equipment')
        .insert({
          ...featured,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating featured equipment:', error);
      return { data: null, error };
    }
  }

  async updateFeaturedEquipment(id: string, updates: Partial<CMSFeaturedEquipment>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_featured_equipment')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating featured equipment:', error);
      return { data: null, error };
    }
  }

  async deleteFeaturedEquipment(id: string) {
    try {
      const { error } = await supabase
        .from('cms_featured_equipment')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting featured equipment:', error);
      return { error };
    }
  }

  // Testimonials
  async getTestimonials(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_testimonials').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async createTestimonial(testimonial: CMSTestimonial) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_testimonials')
        .insert({
          ...testimonial,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating testimonial:', error);
      return { data: null, error };
    }
  }

  async updateTestimonial(id: string, updates: Partial<CMSTestimonial>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_testimonials')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating testimonial:', error);
      return { data: null, error };
    }
  }

  async deleteTestimonial(id: string) {
    try {
      const { error } = await supabase
        .from('cms_testimonials')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      return { error };
    }
  }

  // Site Settings
  async getSiteSettings() {
    try {
      const { data, error } = await supabase
        .from('cms_site_settings')
        .select('*')
        .order('setting_key', { ascending: true });
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching site settings:', error);
      return { data: null, error };
    }
  }

  async getSettingByKey(key: string) {
    try {
      const { data, error } = await supabase
        .from('cms_site_settings')
        .select('*')
        .eq('setting_key', key)
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching setting:', error);
      return { data: null, error };
    }
  }

  async updateSetting(key: string, value: any, type?: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_site_settings')
        .upsert({
          setting_key: key,
          setting_value: typeof value === 'object' ? value : { value },
          setting_type: type || 'text',
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating setting:', error);
      return { data: null, error };
    }
  }

  // Footer Sections
  async getFooterSections(activeOnly = false) {
    return withMissingRelationFallback(async () => {
      let query = supabase.from('cms_footer_sections').select('*');

      if (activeOnly) {
        query = query.eq('is_active', true);
      }

      query = query.order('display_order', { ascending: true });

      return await query;
    }, []);
  }

  async createFooterSection(section: CMSFooterSection) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_footer_sections')
        .insert({
          ...section,
          created_by: user?.id,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error creating footer section:', error);
      return { data: null, error };
    }
  }

  async updateFooterSection(id: string, updates: Partial<CMSFooterSection>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_footer_sections')
        .update({
          ...updates,
          updated_by: user?.id
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating footer section:', error);
      return { data: null, error };
    }
  }

  async deleteFooterSection(id: string) {
    try {
      const { error } = await supabase
        .from('cms_footer_sections')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting footer section:', error);
      return { error };
    }
  }

  // Page Meta
  async getPageMeta(slug?: string) {
    try {
      if (slug) {
        const { data, error } = await supabase
          .from('cms_page_meta')
          .select('*')
          .eq('page_slug', slug)
          .single();
        
        if (error) throw error;
        return { data, error: null };
      } else {
        const { data, error } = await supabase
          .from('cms_page_meta')
          .select('*');
        
        if (error) throw error;
        return { data, error: null };
      }
    } catch (error) {
      console.error('Error fetching page meta:', error);
      return { data: null, error };
    }
  }

  async updatePageMeta(slug: string, meta: Partial<CMSPageMeta>) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from('cms_page_meta')
        .upsert({
          page_slug: slug,
          ...meta,
          updated_by: user?.id
        })
        .select()
        .single();
      
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error updating page meta:', error);
      return { data: null, error };
    }
  }

  // Media
  async getMedia(folder?: string) {
    try {
      let query = supabase.from('cms_media').select('*');
      
      if (folder) {
        query = query.eq('folder', folder);
      }
      
      query = query.order('created_at', { ascending: false });
      
      const { data, error } = await query;
      if (error) throw error;
      return { data, error: null };
    } catch (error) {
      console.error('Error fetching media:', error);
      return { data: null, error };
    }
  }

  async uploadMedia(file: File, folder = 'general', altText?: string) {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Upload file to Supabase Storage
      // Sanitize filename: remove non-ASCII chars, keep only safe characters
      const safeName = file.name
        .replace(/\s+/g, '-')           // spaces → hyphens
        .replace(/[^a-zA-Z0-9._-]/g, '') // remove unsafe chars
        .replace(/\.+/g, '.')            // collapse multiple dots
        .replace(/^[.\-_]+/, '')         // strip leading separators
        .toLowerCase();
      const fileName = `${Date.now()}-${safeName || 'unnamed'}`;
      const filePath = `cms/${folder}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('media')
        .upload(filePath, file);

      if (uploadError) {
        console.error('Storage upload error:', uploadError);
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('media')
        .getPublicUrl(filePath);

      const profileId = await getUserProfileId(user.id);

      // Save media metadata
      const { data, error } = await supabase
        .from('cms_media')
        .insert({
          file_name: fileName,
          file_url: publicUrl,
          file_type: file.type,
          file_size: file.size,
          alt_text: altText || file.name,
          folder,
          uploaded_by: profileId,
        })
        .select()
        .single();

      if (error) {
        console.error('Database insert error:', error);
        throw error;
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error uploading media:', error);
      return { data: null, error };
    }
  }

  async deleteMedia(id: string, fileUrl: string) {
    try {
      // Extract file path from URL
      const url = new URL(fileUrl);
      const pathParts = url.pathname.split('/storage/v1/object/public/media/');
      const filePath = pathParts[1];

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('media')
        .remove([filePath]);

      if (storageError) console.error('Error deleting file from storage:', storageError);

      // Delete metadata
      const { error } = await supabase
        .from('cms_media')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return { error: null };
    } catch (error) {
      console.error('Error deleting media:', error);
      return { error };
    }
  }

  // Real-time subscriptions
  subscribeToChanges(table: string, callback: (payload: any) => void) {
    const subscription = supabase
      .channel(`${table}_changes`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table },
        callback
      )
      .subscribe();

    return subscription;
  }

  unsubscribe(subscription: any) {
    supabase.removeChannel(subscription);
  }
}

export const cmsService = new CMSServiceClass();